import handleGetItemsResponse from 'owa-fileprovider-items-service/lib/getFileProviderItems/handleGetItemsResponse';
import Message from 'owa-service/lib/contract/Message';
import SearchAttachmentDataProviderItemsResponse from 'owa-service/lib/contract/SearchAttachmentDataProviderItemsResponse';
import setCurrentSearchQueryId from 'owa-search/lib/mutators/setCurrentSearchQueryId';
import setNextSearchQueryId from 'owa-search/lib/mutators/setNextSearchQueryId';
import { createFailedAttachmentDataProviderItemsResponse } from 'owa-fileprovider-items-service/lib/getFileProviderItems/createAttachmentDataProviderItemsResponse';
import { createResponseFunc } from 'owa-fileprovider-items-service/lib/getFileProviderItems/getMailMessageFileProviderItems';
import { getGuid } from 'owa-guid';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';
import { getScenarioStore, setLatestTraceId, SearchScenarioId } from 'owa-search';
import { lazyAddSearchRequestInstrumentation } from 'owa-search-diagnostics';
import { SearchScenarioStore } from 'owa-search/lib/store/schema/SearchStore';
import { trace } from 'owa-trace';
import {
    buildInstrumentation,
    lazyLogResponseNeverRendered,
    lazyLogResponseReceived,
    lazyLogResponseRendered,
    SubstrateSearchRequest,
    SubstrateSearchResponse,
    substrateSearchService,
    EntityRequest,
    SearchSortOrder,
    getTimeZone,
    getSearchResultSet,
    convertSearchResultMessageToMessageType,
    SearchResultMessage,
    SearchResultSet,
    SearchRequestInstrumentation,
    SubstrateSearchScenario,
} from 'owa-search-service';

const SUCCESS_STATUS_CODE = 200;

export default async function searchMailMessageFileProviderItems(
    query: string
): Promise<SearchAttachmentDataProviderItemsResponse> {
    if (query.length == 0) {
        throw new Error(
            'FilePicker - Mail Messages: StartSearch should not be called with an empty search!!'
        );
    }

    const searchStore = getScenarioStore(SearchScenarioId.FilesPicker);

    // We're starting a new search query for the first page so we need to update the query IDs.
    setCurrentSearchQueryId(searchStore.nextSearchQueryId, SearchScenarioId.FilesPicker);
    setNextSearchQueryId(getGuid(), SearchScenarioId.FilesPicker);

    // get the owa-service header for timeZone info
    const jsonHeader = getJsonRequestHeader();

    const substrateSearchRequest: SubstrateSearchRequest = {
        Cvid: getGuid(),
        Scenario: {
            Name: SubstrateSearchScenario.MailEmailAttachmentPicker,
        },
        TimeZone: getTimeZone(jsonHeader),
        TextDecorations: 'Off',
        EntityRequests: createEntityRequests(query),
    };

    /**
     * If a search E2E datapoint was specified, we want to add a
     * checkmark to record the pre-request JavaScript time and request
     * JavaScript thread time.
     */
    const preRequestJSTime = 0;
    const requestJSThreadTime = 0;

    //TODO VSO#34628: end search e2e datapoint checkmark

    const clientRequestStart = new Date();

    const result = await substrateSearchService(
        substrateSearchRequest,
        searchStore.searchSessionGuid
    );

    // if result is null or incomplete, just return
    if (!result || result.length != 2) {
        //TODO VSO#34628: end search e2e datapoint

        //TODO VSO#33289: log error response correctly
        trace.warn('3S Response for search mail messages failed');
        const errorResponse: Response = {
            body: null,
            headers: null,
            ok: false,
            redirected: false,
            status: 1, //Generic failure
            statusText: 'Get mail message items error',
            type: null,
            url: null,
            clone: null,
            bodyUsed: null,
            arrayBuffer: null,
            blob: null,
            formData: null,
            json: null,
            text: null,
            trailer: null,
        };

        return handleGetItemsResponse(
            null /* result */,
            errorResponse,
            createResponseFunc,
            createFailedAttachmentDataProviderItemsResponse
        );
    }

    // The result array contains the response.body in [0] place
    // When the request fails the body will be undefined and we should check for
    // successful response from the response code in searchInstrumentation.
    const responseCode = result[0].status;
    const isSuccessfulResponse = responseCode == SUCCESS_STATUS_CODE;

    // Extract errror string from response (if it's present).
    const errorMessage: string = isSuccessfulResponse ? null : result[0].statusText;

    const searchInstrumentation = addSearchInstrumentation(
        result,
        query,
        errorMessage,
        clientRequestStart,
        searchStore,
        preRequestJSTime,
        requestJSThreadTime
    );

    if (isSuccessfulResponse) {
        const responseBody = <SubstrateSearchResponse>await result[0].json();
        const messageList = convertSearchResultToMessages(
            getSearchResultSet(responseBody),
            substrateSearchRequest
        );

        lazyLogResponseRendered.importAndExecute(
            searchInstrumentation.SearchTraceID,
            new Date(),
            SubstrateSearchScenario.MailEmailAttachmentPicker
        );

        return handleGetItemsResponse(
            messageList,
            null /* errorResponse */,
            createResponseFunc,
            createFailedAttachmentDataProviderItemsResponse
        );
    } else {
        //TODO VSO#34628: end search e2e datapoint

        //TODO VSO#33289: correctly log response
        trace.warn(`3S Response for search mail messages failed with ${errorMessage}`);
        const errorResponse: Response = {
            body: null,
            headers: null,
            ok: false,
            redirected: false,
            status: 1, //Generic failure
            statusText: errorMessage,
            type: null,
            url: null,
            clone: null,
            bodyUsed: null,
            arrayBuffer: null,
            blob: null,
            formData: null,
            json: null,
            text: null,
            trailer: null,
        };

        lazyLogResponseNeverRendered.importAndExecute(
            searchInstrumentation.SearchTraceID,
            SubstrateSearchScenario.MailEmailAttachmentPicker
        );

        return handleGetItemsResponse(
            null /* result */,
            errorResponse,
            createResponseFunc,
            createFailedAttachmentDataProviderItemsResponse
        );
    }
}

function convertSearchResultToMessages(
    resultSet: SearchResultSet,
    request: SubstrateSearchRequest
): Message[] {
    if (!resultSet || !resultSet.Results || resultSet.Results.length === 0) {
        return [];
    } else {
        const messageList = [];

        const searchResults = resultSet.Results;
        for (const searchResult of searchResults) {
            const messageType = convertSearchResultMessageToMessageType(
                searchResult.Source as SearchResultMessage,
                request
            );
            messageType.InstanceKey = searchResult.ReferenceId;
            messageList.push(messageType);
        }
        return messageList;
    }
}

function createEntityRequests(queryString: string): EntityRequest[] {
    const timeSort: SearchSortOrder = {
        Field: 'Time',
        SortDirection: 'Desc',
    };

    const entityRequests: EntityRequest[] = [
        {
            EntityType: 'Message',
            Filter: null,
            From: 0,
            PropertySet: 'ProvenanceOptimized',
            Provenances: ['Exchange'],
            Query: {
                QueryString: queryString,
            },
            RefiningQueries: null,
            Size: 50,
            Sort: [timeSort],
        },
    ];

    return entityRequests;
}

function addSearchInstrumentation(
    result: any,
    query: string,
    errorMessage: string,
    clientRequestStart: Date,
    searchStore: SearchScenarioStore,
    preRequestJSTime: number,
    requestJSThreadTime: number
): SearchRequestInstrumentation {
    // return null if the response is null or incomplete
    if (!result || result.length != 2) {
        return null;
    }

    // Push instrumentation to diagnostics store and panel
    const searchInstrumentation = buildInstrumentation(
        result[0],
        query,
        errorMessage,
        'SubstrateSearch',
        clientRequestStart,
        new Date(),
        searchStore.currentSearchQueryId,
        new Headers(result[1]),
        0,
        SubstrateSearchScenario.MailEmailAttachmentPicker
    );
    searchInstrumentation.ClientJSPreRequestTime = preRequestJSTime;
    searchInstrumentation.ClientJSRequestThreadTime = requestJSThreadTime;

    // set the latestTraceId in the search Store for further instrumentation directly from common control

    setLatestTraceId(
        searchInstrumentation.SearchTraceID,
        SearchScenarioId.FilesPicker,
        false /* isQFTraceId */
    );

    lazyAddSearchRequestInstrumentation
        .import()
        .then(addSearchRequestInstrumentation =>
            addSearchRequestInstrumentation(searchInstrumentation)
        );

    //TODO VSO#34628: setTimeout resource timing entry

    lazyLogResponseReceived.importAndExecute(
        searchInstrumentation.SearchTraceID,
        searchInstrumentation.ClientNetworkTime,
        searchInstrumentation.ClientHttpStatus,
        new Date()
    );

    return searchInstrumentation;
}
